import {
  Partnership,
  PartnershipStatus,
  PartnershipStatusHelper,
} from "../../../domain/models/Partnership";

/**
 * Converts Partnership domain models to/from Firebase Realtime Database format.
 */
export class PartnershipConverter {
  /**
   * Converts Firebase snapshot data to Partnership domain model.
   *
   * @param data The Firebase snapshot data from partnerships/<id>/profile
   * @param identifier The partnership identifier
   * @param loggedInUserId The current logged-in user's identifier
   * @returns Partnership object or null if data is invalid
   */
  static fromFirebase(
    data: Record<string, any> | null | undefined,
    identifier: string,
    loggedInUserId: string,
  ): Partnership | null {
    if (!data) {
      return null;
    }

    // Determine partner identifier
    // The partner is whoever is NOT the logged-in user
    let partnerIdentifier: string | undefined;

    const partner1 = data.partner1;
    const partner2 = data.partner2;

    if (partner1 && partner1 !== loggedInUserId) {
      partnerIdentifier = partner1;
    } else if (partner2 && partner2 !== loggedInUserId) {
      partnerIdentifier = partner2;
    }

    // Parse status from partnersStatuses map
    const partnersStatuses = data.partnersStatuses || {};
    const statusValue = partnersStatuses[loggedInUserId];
    const status = PartnershipStatusHelper.fromFirebaseValue(statusValue);

    // Parse matched cards identifiers (if any)
    const matchedCardsByIdentifier: Record<string, number> = {};
    // Note: Matched cards are stored separately in partnerships/<id>/matches
    // This would be loaded separately if needed

    return {
      identifier,
      partnerIdentifier,
      status,
      coupledAtTime: data.coupledAtTime,
      matchedCardsByIdentifier,
      invitationCode: data.invitationCode || "",
      characterId:
        typeof data.characterId === "string" ? data.characterId : undefined,
    };
  }

  /**
   * Creates Firebase update object for a new fictional-character partnership.
   * No invitation code is involved; the character occupies partner2.
   * Also selects the character for play (selectedCharacter + isPlayingAlone).
   *
   * @param userId The user identifier creating the partnership
   * @param characterId The fictional character identifier (char_…)
   * @param partnershipId The new partnership identifier
   * @param timestamp Creation timestamp in seconds
   * @returns Firebase update object with full paths
   */
  static createCharacterToFirebase(
    userId: string,
    characterId: string,
    partnershipId: string,
    timestamp: number,
  ): Record<string, unknown> {
    const updates: Record<string, unknown> = {};
    const basePath = `partnerships/${partnershipId}/profile`;

    updates[`${basePath}/partner1`] = userId;
    updates[`${basePath}/partner2`] = characterId;
    updates[`${basePath}/characterId`] = characterId;
    updates[`${basePath}/status`] = "character";
    updates[`${basePath}/partnersStatuses/${userId}`] = "character";
    updates[`${basePath}/coupledAtTime`] = timestamp;

    updates[`users/${userId}/profile/partnerships/character/${partnershipId}`] =
      true;
    updates[`users/${userId}/profile/selectedCharacter`] = characterId;
    updates[`users/${userId}/profile/isPlayingAlone`] = true;

    return updates;
  }

  /**
   * Creates Firebase update object for disconnecting from a fictional-character
   * partnership. Mirrors disconnectToFirebase but has no partner side to update
   * and clears the character selection.
   *
   * @param partnershipId The partnership identifier
   * @param userId The user identifier
   * @param clearSelectedCharacter Whether selectedCharacter currently points at
   *   this partnership's character and must be cleared
   * @returns Firebase update object with full paths
   */
  static disconnectCharacterToFirebase(
    partnershipId: string,
    userId: string,
    clearSelectedCharacter: boolean,
  ): Record<string, unknown> {
    const updates: Record<string, unknown> = {};

    updates[
      `partnerships/${partnershipId}/profile/partnersStatuses/${userId}`
    ] = "invalid";
    updates[`partnerships/${partnershipId}/profile/status`] = "invalid";
    updates[`users/${userId}/profile/partnerships/invalid/${partnershipId}`] =
      true;
    updates[`users/${userId}/profile/partnerships/character/${partnershipId}`] =
      null;

    // Clear deck state
    updates[`users/${userId}/profile/lastPartnershipsVisits/${partnershipId}`] =
      null;
    updates[
      `users/${userId}/profile/partnershipsDeckTopCardIdentifiers/${partnershipId}`
    ] = null;
    updates[
      `users/${userId}/profile/partnershipsNumberOfCardsInDeckLeft/${partnershipId}`
    ] = null;
    updates[
      `users/${userId}/profile/partnershipsLastMatchesScreenVisit/${partnershipId}`
    ] = null;

    if (clearSelectedCharacter) {
      updates[`users/${userId}/profile/selectedCharacter`] = null;
    }

    return updates;
  }

  /**
   * Converts Partnership to Firebase update format for creating a new partnership.
   *
   * @param partnership The Partnership object
   * @param userId The user identifier creating the partnership
   * @param isPlayingAlone Whether this is a play-alone partnership
   * @returns Firebase update object with full paths
   */
  static toFirebaseForCreation(
    partnership: Partnership,
    userId: string,
    isPlayingAlone: boolean = false,
  ): Record<string, unknown> {
    const updates: Record<string, unknown> = {};
    const basePath = `partnerships/${partnership.identifier}/profile`;

    // Set partnership profile
    updates[`${basePath}/partner1`] = userId;

    if (isPlayingAlone) {
      updates[`${basePath}/status`] = "playingAlone";
      updates[`${basePath}/partnersStatuses/${userId}`] = "playingAlone";
    } else {
      updates[`${basePath}/status`] = "pending";
      updates[`${basePath}/partnersStatuses/${userId}`] = "pending";
    }

    updates[`${basePath}/invitationCode`] = partnership.invitationCode;

    // Link invitation code to partnership
    updates[`invitationCodes/${partnership.invitationCode}/partnership`] =
      partnership.identifier;

    // Update user's partnerships
    if (isPlayingAlone) {
      updates[
        `users/${userId}/profile/partnerships/playingAlone/${partnership.identifier}`
      ] = true;
    } else {
      updates[
        `users/${userId}/profile/partnerships/pending/${partnership.identifier}`
      ] = true;
    }

    // Set user's invitation code
    updates[`users/${userId}/profile/invitationCode`] =
      partnership.invitationCode;

    return updates;
  }

  /**
   * Converts status update to Firebase format.
   *
   * @param partnershipId The partnership identifier
   * @param userId The user identifier
   * @param status The new status
   * @returns Firebase update object with full paths
   */
  static statusToFirebase(
    partnershipId: string,
    userId: string,
    status: PartnershipStatus,
  ): Record<string, unknown> {
    const updates: Record<string, unknown> = {};
    const firebaseStatus = PartnershipStatusHelper.toFirebaseValue(status);

    updates[
      `partnerships/${partnershipId}/profile/partnersStatuses/${userId}`
    ] = firebaseStatus;
    updates[`partnerships/${partnershipId}/profile/status`] = firebaseStatus;

    // Update user's partnership status mappings
    switch (status) {
      case PartnershipStatus.Pending:
        updates[
          `users/${userId}/profile/partnerships/pending/${partnershipId}`
        ] = true;
        updates[
          `users/${userId}/profile/partnerships/active/${partnershipId}`
        ] = null;
        updates[
          `users/${userId}/profile/partnerships/currentlyPlaying/${partnershipId}`
        ] = null;
        break;

      case PartnershipStatus.Active:
        updates[
          `users/${userId}/profile/partnerships/pending/${partnershipId}`
        ] = null;
        updates[
          `users/${userId}/profile/partnerships/active/${partnershipId}`
        ] = true;
        updates[
          `users/${userId}/profile/partnerships/currentlyPlaying/${partnershipId}`
        ] = null;
        break;

      case PartnershipStatus.Selected:
        updates[
          `users/${userId}/profile/partnerships/pending/${partnershipId}`
        ] = null;
        updates[
          `users/${userId}/profile/partnerships/active/${partnershipId}`
        ] = null;
        updates[
          `users/${userId}/profile/partnerships/currentlyPlaying/${partnershipId}`
        ] = true;
        break;

      case PartnershipStatus.PlayingAlone:
        updates[
          `users/${userId}/profile/partnerships/playingAlone/${partnershipId}`
        ] = true;
        updates[`users/${userId}/profile/isPlayingAlone`] = true;
        break;

      case PartnershipStatus.Invalid:
        updates[
          `users/${userId}/profile/partnerships/invalid/${partnershipId}`
        ] = true;
        updates[
          `users/${userId}/profile/partnerships/active/${partnershipId}`
        ] = null;
        updates[
          `users/${userId}/profile/partnerships/currentlyPlaying/${partnershipId}`
        ] = null;
        break;
    }

    return updates;
  }

  /**
   * Converts matched card identifiers to Firebase format for storage.
   *
   * @param partnershipId The partnership identifier
   * @param cardIdentifiers Array of matched card identifiers
   * @param timestamp The match timestamp
   * @returns Firebase update object with full paths
   */
  static matchedCardsToFirebase(
    partnershipId: string,
    cardIdentifiers: string[],
    timestamp: number,
  ): Record<string, unknown> {
    const updates: Record<string, unknown> = {};

    cardIdentifiers.forEach((cardId) => {
      updates[`partnerships/${partnershipId}/matches/${cardId}`] = timestamp;
    });

    return updates;
  }

  /**
   * Converts matched cards with individual timestamps to Firebase format.
   * Used when committing held character matches, whose reveal times differ.
   *
   * @param partnershipId The partnership identifier
   * @param timestampsByCardId Record of card identifier to match timestamp (seconds)
   * @returns Firebase update object with full paths
   */
  static matchedCardsWithTimestampsToFirebase(
    partnershipId: string,
    timestampsByCardId: Record<string, number>,
  ): Record<string, unknown> {
    const updates: Record<string, unknown> = {};

    Object.entries(timestampsByCardId).forEach(([cardId, timestamp]) => {
      updates[`partnerships/${partnershipId}/matches/${cardId}`] = timestamp;
    });

    return updates;
  }

  /**
   * Parses matched cards from Firebase matches node.
   *
   * @param matchesData The data from partnerships/<id>/matches
   * @returns Record of matched card identifiers to timestamps
   */
  static matchedCardsFromFirebase(
    matchesData: Record<string, number> | null | undefined,
  ): Record<string, number> {
    if (!matchesData) {
      return {};
    }

    const matchedCards: Record<string, number> = {};
    Object.entries(matchesData).forEach(([cardId, timestamp]) => {
      if (typeof timestamp === "number") {
        matchedCards[cardId] = timestamp;
      }
    });

    return matchedCards;
  }

  /**
   * Creates Firebase update object for disconnecting from a partnership.
   *
   * @param partnershipId The partnership identifier
   * @param userId The user identifier
   * @param partnerId The partner's identifier
   * @returns Firebase update object with full paths
   */
  static disconnectToFirebase(
    partnershipId: string,
    userId: string,
    partnerId?: string,
  ): Record<string, unknown> {
    const updates: Record<string, unknown> = {};

    // Mark partnership as invalid for the user
    updates[
      `partnerships/${partnershipId}/profile/partnersStatuses/${userId}`
    ] = "invalid";
    updates[`users/${userId}/profile/partnerships/invalid/${partnershipId}`] =
      true;
    updates[`users/${userId}/profile/partnerships/active/${partnershipId}`] =
      null;
    updates[
      `users/${userId}/profile/partnerships/currentlyPlaying/${partnershipId}`
    ] = null;

    // Clear deck state
    updates[`users/${userId}/profile/lastPartnershipsVisits/${partnershipId}`] =
      null;
    updates[
      `users/${userId}/profile/partnershipsDeckTopCardIdentifiers/${partnershipId}`
    ] = null;
    updates[
      `users/${userId}/profile/partnershipsNumberOfCardsInDeckLeft/${partnershipId}`
    ] = null;
    updates[
      `users/${userId}/profile/partnershipsLastMatchesScreenVisit/${partnershipId}`
    ] = null;

    // If partner exists, update their side too
    if (partnerId) {
      updates[
        `partnerships/${partnershipId}/profile/partnersStatuses/${partnerId}`
      ] = "invalid";
      updates[
        `users/${partnerId}/profile/partnerships/invalid/${partnershipId}`
      ] = true;
      updates[
        `users/${partnerId}/profile/partnerships/active/${partnershipId}`
      ] = null;
      updates[
        `users/${partnerId}/profile/partnerships/currentlyPlaying/${partnershipId}`
      ] = null;
    }

    return updates;
  }

  /**
   * Creates Firebase update object for deleting pending partnerships.
   *
   * @param userId The user identifier
   * @param pendingPartnerships Array of pending partnership IDs with their invitation codes
   * @returns Firebase update object with full paths
   */
  static deletePendingPartnershipsToFirebase(
    userId: string,
    pendingPartnerships: Array<{ id: string; invitationCode: string }>,
  ): Record<string, unknown> {
    const updates: Record<string, unknown> = {};

    // Clear all pending partnerships at once
    updates[`users/${userId}/profile/partnerships/pending`] = null;

    // Delete each partnership and its invitation code
    pendingPartnerships.forEach(({ id, invitationCode }) => {
      updates[`partnerships/${id}`] = null;
      updates[`invitationCodes/${invitationCode}`] = null;
    });

    return updates;
  }

  /**
   * Creates Firebase update object for saving last partnership visit.
   *
   * @param userId The user identifier
   * @param partnershipId The partnership identifier
   * @param timestamp The visit timestamp
   * @param deckTopCardId Optional deck top card identifier
   * @param numberOfCardsLeft Optional number of cards left in deck
   * @returns Firebase update object with full paths
   */
  static visitToFirebase(
    userId: string,
    partnershipId: string,
    timestamp: number,
    deckTopCardId?: string | null,
    numberOfCardsLeft?: number,
  ): Record<string, unknown> {
    const updates: Record<string, unknown> = {};

    updates[`users/${userId}/profile/lastPartnershipsVisits/${partnershipId}`] =
      timestamp;

    if (deckTopCardId !== undefined) {
      updates[
        `users/${userId}/profile/partnershipsDeckTopCardIdentifiers/${partnershipId}`
      ] = deckTopCardId;
    }

    if (numberOfCardsLeft !== undefined) {
      updates[
        `users/${userId}/profile/partnershipsNumberOfCardsInDeckLeft/${partnershipId}`
      ] = numberOfCardsLeft;
    }

    return updates;
  }

  static deckStateToFirebase(
    userId: string,
    partnershipId: string,
    deckTopCardId: string | null | undefined,
    numberOfCardsLeft: number,
  ): Record<string, unknown> {
    const updates: Record<string, unknown> = {};

    if (deckTopCardId !== undefined) {
      updates[
        `users/${userId}/profile/partnershipsDeckTopCardIdentifiers/${partnershipId}`
      ] = deckTopCardId;
    }
    updates[
      `users/${userId}/profile/partnershipsNumberOfCardsInDeckLeft/${partnershipId}`
    ] = numberOfCardsLeft;

    return updates;
  }
}
